import { pickedApiRequest } from './request'
import { readReferralProof } from '@utils/referralAttribution'
import '@groupResources/signup-source/tc-signup-source.js'

// Where this visitor first came from, saved on the new organisation. Read
// from window: babel's polyfill imports make the shared ES5 file an ES
// module to webpack, so a default import of it is undefined.
function withSource (payload) {
  let source = null
  try { source = window.TCSignupSource.forSignup() } catch (e) {}
  try {
    const first = JSON.parse(window.localStorage.getItem('tc.introducer.first'))
    if (first && first.expires > Date.now()) { source = { ...(source || {}), partnerCode: first.code }; payload = { ...payload, referralProof: null } }
  } catch (e) {}
  return source ? { ...payload, signupSource: source } : payload
}

/**
 * Employer sign-up, sign-in and link endpoints (see
 * V/knowledge/testcandidates-remediation/EMPLOYER-AUTH-BUILD.md for the
 * contract). Every function resolves to the response body.
 */
export default {
  providerConnections (employerId) {
    return pickedApiRequest.get(`assessment/employers/${encodeURIComponent(employerId)}/providers`)
      .then(response => response.data.data)
  },
  linkProvider (employerId, provider, idToken) {
    if (!['google', 'microsoft'].includes(provider)) return Promise.reject(new Error('Unknown provider'))
    return pickedApiRequest.post(`assessment/employers/${encodeURIComponent(employerId)}/${provider}-link`, { idToken })
      .then(response => response.data.data)
  },
  /**
   * Disconnect one provider from the signed-in employer's own account. The
   * body confirms the choice; the response carries the remaining providers
   * and, when something changed, a new session token for this device (the
   * server signs every other device out).
   */
  unlinkProvider (employerId, provider) {
    if (!['google', 'microsoft'].includes(provider)) return Promise.reject(new Error('Unknown provider'))
    return pickedApiRequest.delete(`assessment/employers/${encodeURIComponent(employerId)}/providers/${provider}`, { data: { confirm: true } })
      .then(response => response.data.data)
  },
  linkMicrosoft (employerId, idToken) {
    return pickedApiRequest.post(`assessment/employers/${encodeURIComponent(employerId)}/microsoft-link`, { idToken })
      .then(response => response.data.data)
  },
  emailStatus (email) {
    return pickedApiRequest.post('assessment/employers/email-status', { email })
      .then(response => response.data.data)
  },

  signUp (payload) {
    return pickedApiRequest.post('assessment/employers/sign-up', withSource({ ...payload, referralProof: readReferralProof() }))
      .then(response => response.data)
  },

  magicLink (email, device) {
    return pickedApiRequest.post('assessment/employers/magic-link', { email, device })
      .then(response => response.data)
  },

  peekLink (employerId, code) {
    return pickedApiRequest.get(`assessment/employers/${encodeURIComponent(employerId)}/auth-links/${encodeURIComponent(code)}`)
      .then(response => response.data.data)
  },

  exchangeLink (employerId, code, device) {
    return pickedApiRequest.post(`assessment/employers/${encodeURIComponent(employerId)}/auth-links/${encodeURIComponent(code)}`, { action: 'exchange', device })
      .then(response => response.data)
  },

  approveLink (employerId, code, device) {
    return pickedApiRequest.post(`assessment/employers/${encodeURIComponent(employerId)}/auth-links/${encodeURIComponent(code)}`, { action: 'approve', device })
      .then(response => response.data.data)
  },

  pickup (employerId, requestId) {
    return pickedApiRequest.get(`assessment/employers/${encodeURIComponent(employerId)}/auth-requests/${encodeURIComponent(requestId)}`)
      .then(response => response.data.data)
  },

  providerEmail (provider, idToken) {
    return pickedApiRequest.post(`assessment/employers/${provider}-email`, { idToken })
      .then(response => response.data.data)
  },

  providerSignIn (provider, idToken) {
    return pickedApiRequest.post(`assessment/employers/${provider}`, { idToken })
      .then(response => response.data.data)
  },

  providerSignUp (payload) {
    return pickedApiRequest.post('assessment/employers/provider-sign-up', withSource({ ...payload, referralProof: readReferralProof() }))
      .then(response => response.data)
  },

  resendVerification (employerId) {
    return pickedApiRequest.post(`assessment/employers/${encodeURIComponent(employerId)}/resend-verification`)
      .then(response => response.data.data)
  },

  joinInfo (token) {
    return pickedApiRequest.get(`assessment/join/${encodeURIComponent(token)}`)
      .then(response => response.data.data)
  },

  joinRequest (token, payload) {
    return pickedApiRequest.post(`assessment/join/${encodeURIComponent(token)}`, payload)
      .then(response => response.data.data)
  },

  joinLink (organisationId) {
    return pickedApiRequest.post(`assessment/organisations/${encodeURIComponent(organisationId)}/join-link`)
      .then(response => response.data.data)
  },

  inviteCandidate (organisationId, jobUuid, email) {
    return pickedApiRequest.post(`assessment/organisations/${encodeURIComponent(organisationId)}/invite-candidates`, {
      candidates: [{ email, name: '' }],
      jobUuid
    })
      .then(response => response.data.data)
  }
}
